import type {
  ChatPlayerAuthoredMessage,
  ChatPlayerAuthoredPart,
  ChatPlayerScenarioSource,
} from "@ui/components/core/ChatPlayer";
import type {
  CmsChatScenario,
  CmsChatScenarioMessage,
  CmsChatScenarioPart,
} from "@ui/components/core/ChatScenario/cms";

function mapPart(part: CmsChatScenarioPart, index: number, scope: string) {
  const id = `${scope}-part-${index}`;

  if ((part.type === "text" || part.type === "speech") && part.content) {
    return {
      id,
      type: part.type,
      content: part.content,
    } satisfies ChatPlayerAuthoredPart;
  }

  if (part.type !== "tool" || !part.data?.loadingText) {
    return null;
  }

  const successText = part.data.successText ?? part.data.errorText ?? part.data.loadingText;
  const result = part.data.errorText && !part.data.successText ? "error" : "success";

  return {
    id,
    type: "tool",
    name: part.name ?? "simple",
    data: {
      loadingText: part.data.loadingText,
      successText,
      errorText: part.data.errorText ?? undefined,
      result,
      successImage: part.data.successImage ?? undefined,
    },
  } satisfies ChatPlayerAuthoredPart;
}

function mapMessage(message: CmsChatScenarioMessage, index: number, scope: string) {
  const id = `${scope}-message-${index}`;
  const parts = message.parts.flatMap((part, partIndex) => {
    const mappedPart = mapPart(part, partIndex, id);

    return mappedPart ? [mappedPart] : [];
  });

  if (parts.length === 0) {
    return null;
  }

  return {
    id,
    role: message.role,
    parts,
  } satisfies ChatPlayerAuthoredMessage;
}

function mapMessages(messages: CmsChatScenarioMessage[] | null | undefined, scope: string) {
  return (
    messages?.flatMap((message, index) => {
      const mappedMessage = mapMessage(message, index, scope);

      return mappedMessage ? [mappedMessage] : [];
    }) ?? []
  );
}

export function createChatPlayerSourceFromCmsChatScenario(chatScenario: CmsChatScenario | null | undefined) {
  if (!chatScenario) {
    return null;
  }

  const initialMessages = mapMessages(chatScenario.initialMessages, "initial");
  const messages = mapMessages(chatScenario.messages, "message");

  if (initialMessages.length === 0 && messages.length === 0) {
    return null;
  }

  return {
    id: chatScenario.agentName,
    initialMessages,
    messages,
  } satisfies ChatPlayerScenarioSource;
}
